import styled from 'styled-components';
import { Row, Button, Input } from 'antd';
import {
  DeleteOutlined,
  MergeCellsOutlined,
  CopyOutlined,
  SearchOutlined,
  DownOutlined,
  FilterOutlined
} from '@ant-design/icons';
import TextBox from 'components/TextBox';
import CustomSelect from 'components/CustomSelect';
import CustomTable from 'components/Tables/CustomTable';
import { IToken, IItemWidth } from 'store/ducks/globalTypes';
import { themesList } from 'constants/themesList';
import CustomCheckbox from 'components/CustomCheckbox';

const { TextArea } = Input;

// Helper function to extract theme values
const getThemeValues = (scheme?: string, token?: unknown) => {
  const primaryColor =
    scheme && themesList[scheme as keyof typeof themesList]
      ? (() => {
          const theme = themesList[scheme as keyof typeof themesList];
          return theme && typeof theme === 'object' && 'colorPrimary' in theme
            ? theme.colorPrimary
            : '#c1c1c1';
        })()
      : '#c1c1c1';

  const bgContainer =
    ((token as Record<string, unknown>)?.colorBgContainer as string) ||
    '#f3f3f3';
  const bgMask =
    ((token as Record<string, unknown>)?.colorBgMask as string) || '#dadada';

  return { primaryColor, bgContainer, bgMask };
};

export const CheckboxItem = styled('div')<IItemWidth>`
  height: 60px;
  width: ${(props) => (props.width ? `${props.width}px` : '85px')};
  padding: ${(props) => `0 ${props.token.padding}px`};

  /* Move Merge and Copy checkboxes to the right */
  &:nth-child(3) {
    margin-left: -130px;

    &::after {
      right: 70px;
    }
  }

  &:nth-child(4) {
    padding-left: 35px;
  }
`;

export const ContentWrapper = styled('div')<IToken>`
  width: 100%;

  .ant-table-wrapper .ant-table {
    .ant-table-filter-column {
      flex-direction: row-reverse;

      .ant-dropdown-trigger {
        width: 60px;
        margin-inline: 0 5px;
        background-image: none;
        top: 5px;
        cursor: pointer;

        span svg {
          display: none;
        }

        &:hover {
          background-color: transparent;
        }
      }

      /* Show our custom scope dropdown icon */
      .ant-dropdown-trigger:has([data-scope-icon]) span svg,
      .ant-dropdown-trigger [data-scope-icon] svg {
        display: inline;
      }
    }
    .ant-table-content {
      border-radius: 10px;
    }
    .ant-table-thead tr .ant-table-cell {
      white-space: nowrap;
    }
  }

  /* Force high z-index for scope and category dropdowns */
  .scope-filter-dropdown {
    z-index: 9999;
    position: absolute;
  }

  .category-filter-dropdown {
    z-index: 9999;
    position: absolute;
  }
`;

export const Container = styled('div')`
  padding: 0 10px;
  height: auto;
  min-height: fit-content;
`;

export const ClientContainer = styled('div')`
  padding: 0 10px;
  margin-left: 80px;
`;

export const TableHeaderGrid = styled('div')<{
  isExpanded?: boolean;
  isAurExpanded?: boolean;
  isRegReqExpanded?: boolean;
  dragOffset?: number;
}>`
  display: grid;
  grid-template-columns: ${(props) => {
    const offset = props.dragOffset || 0;
    const aurWidth = props.isAurExpanded ? 650 : 450;
    return `75px ${Math.max(
      aurWidth,
      aurWidth + offset
    )}px 100px 80px 80px 120px 1fr`;
  }};
  gap: 20px;
  align-items: center;
  width: 100%;

  > div:first-child {
    padding-top: 10px;
  }
`;

export const AurInterpretationWrapper = styled('div')<{
  scheme?: string;
  isLastAurInRecord?: boolean;
}>`
  position: relative;
  z-index: 0;

  /* Add horizontal border only for lilac and deepBlue themes and only for the last AUR in a record */
  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: -800px;
    right: -30px;
    height: 1px;
    background-color: ${(props) => {
      if (
        props.isLastAurInRecord &&
        props.scheme &&
        (props.scheme === 'lilacTheme' ||
          props.scheme === 'deepBlueTheme' ||
          props.scheme === 'deepNavyTheme')
      ) {
        const currentTheme =
          themesList[props.scheme as keyof typeof themesList];
        return currentTheme?.tableBorder || 'transparent';
      }
      return 'transparent';
    }};
    z-index: 1;
    display: ${(props) => (props.isLastAurInRecord ? 'block' : 'none')};
  }

  padding-bottom: ${(props) =>
    props.isLastAurInRecord &&
    (props.scheme === 'lilacTheme' ||
      props.scheme === 'deepBlueTheme' ||
      props.scheme === 'deepNavyTheme')
      ? '4px'
      : '0'};
`;

export const TableRowGrid = styled('div')<{
  isExpanded?: boolean;
  isAurExpanded?: boolean;
  isRegReqExpanded?: boolean;
  scheme?: string;
  dragOffset?: number;
}>`
  display: grid;
  grid-template-columns: ${(props) => {
    const offset = props.dragOffset || 0;
    const aurWidth = props.isAurExpanded ? 650 : 450;
    return `75px ${Math.max(
      aurWidth,
      aurWidth + offset
    )}px 100px 80px 80px 120px 1fr`;
  }};
  gap: 20px;
  align-items: start;
  width: 100%;
  margin-bottom: 4px;
  min-height: fit-content;
  overflow: visible;
  position: relative;
  z-index: 0;
`;
export const ScopeCheckbox = styled(CustomCheckbox)`
  margin: auto;
`;
export const GridColumn = styled('div')<{ scheme?: string }>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  overflow: visible;
  position: relative;
  // margin-left: 30px;

  span {
    color: ${(props) =>
      props.scheme === 'pinkUITheme' || props.scheme === 'lightUITheme'
        ? '#000000'
        : 'inherit'};
  }

  &:nth-child(3) {
    margin-left: -10px;
  }

  &:nth-child(4) {
    margin-left: 20px;
  }

  &:nth-child(5) {
    margin-left: 10px;
  }

  &:nth-child(6) {
    margin-left: 30px;
  }

  /* Ensure textarea can resize properly */
  textarea {
    position: relative;
    z-index: 1;
  }
`;

export const HeaderGridColumn = styled('div')<{
  scheme?: string;
  isExpanded?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-left: -30px;
  padding-top: 8px;
  width: ${(props) => (props.isExpanded ? 'auto' : 'initial')};
  white-space: pre-line;
  text-align: center;
  line-height: 1.2;
  overflow: visible;
  z-index: 1;
  min-width: 150px;

  /* First column (Scope) should have higher z-index and no pointer blocking */
  &:first-child {
    z-index: 1001 !important;
    pointer-events: none !important;

    /* Allow pointer events only for the scope container */
    > * {
      pointer-events: auto !important;
    }
  }

  span {
    color: ${(props) =>
      props.scheme === 'pinkUITheme' || props.scheme === 'lightUITheme'
        ? '#000000'
        : 'inherit'};
    white-space: pre-line;
    text-align: center;
    line-height: 1.2;
  }

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    right: 65px;
    top: calc(50% + 2px);
    transform: translateY(-50%);
    width: 1px;
    height: 20px;
    background-color: #fcfafa;
  }

  &:nth-child(2) {
    margin-left: -200px;

    &::after {
      right: 65px;
    }
  }

  &:nth-child(3) {
    margin-left: -140px;

    &::after {
      right: 40px;
    }
  }

  &:nth-child(4) {
    margin-left: -120px;
    margin-right: 20px;

    &::after {
      right: 40px;
    }
  }

  &:nth-child(5) {
    margin-left: -180px;

    &::after {
      right: 100px;
    }
  }

  &:nth-child(6) {
    margin-left: -130px;
    z-index: 5;
    min-width: 120px;

    &::after {
      right: 30px;
    }
  }

  &:nth-child(7) {
    margin-left: -180px;
  }

  &:last-child {
    padding-left: 135px;
    white-space: nowrap;

    span {
      white-space: nowrap;
    }
  }
`;

export const MergedColumnSm = styled('div')<IItemWidth>`
  width: ${(props) => (props.width ? `${props.width}px` : '65px')};
  position: relative;
  padding: ${(props) => `0 ${props.token.padding}px`};
  margin-left: 20px;

  /* Specific adjustment for Merge and Copy columns */
  &:nth-of-type(6) {
    /* Merge column */
    margin-left: 20px;
  }
  &:nth-of-type(7) {
    /* Copy column */
    margin-left: 20px;
  }

  span {
    color: ${(props) =>
      props.scheme === 'pinkUITheme' || props.scheme === 'lightUITheme'
        ? '#000000'
        : 'inherit'};
  }

  &:before {
    position: absolute;
    top: 50%;
    inset-inline-end: 0;
    width: 1px;
    height: 1.6em;
    background-color: ${(props) => props.token.colorBgMask};
    transform: translateY(-50%);
    transition: background-color 0.2s;
    content: '';
    display: block;
  }
`;

export const MergedColumnMd = styled('div')<IItemWidth>`
  width: 350px;
  padding: ${(props) => `0 ${props.token.padding}px`};
  margin-left: 20px;

  span {
    color: ${(props) =>
      props.scheme === 'pinkUITheme' || props.scheme === 'lightUITheme'
        ? '#000000'
        : 'inherit'};
  }
`;

export const ClientHeaderColumn = styled('div')<IItemWidth>`
  width: 450px;
  padding: ${(props) => `0 ${props.token.padding}px`};
  margin-left: 20px;

  span {
    color: ${(props) =>
      props.scheme === 'pinkUITheme' || props.scheme === 'lightUITheme'
        ? '#000000'
        : 'inherit'};
  }
`;

export const HeaderSeparator = styled('div')`
  width: 1px;
  background-color: #d9d9d9;
  height: 20px;
  margin: 0 4px;

  /* Move separator before Merge column to the left */
  &:nth-of-type(5) {
    margin-left: 100px;
  }
`;

export const MergedColumnLg = styled('div')<IItemWidth>`
  width: ${(props) => (props.width ? `${props.width}px` : '565px')};
  position: relative;
  padding: ${(props) => `0 ${props.token.padding}px`};

  span {
    color: ${(props) =>
      props.scheme === 'pinkUITheme' || props.scheme === 'lightUITheme'
        ? '#000000'
        : 'inherit'};
  }

  &:before {
    position: absolute;
    top: 50%;
    inset-inline-end: 0;
    width: 1px;
    height: 1.6em;
    background-color: ${(props) => props.token.colorBgMask};
    transform: translateY(-50%);
    transition: background-color 0.2s;
    content: '';
    display: block;
  }
`;

export const CheckboxContainer = styled('div')`
  min-width: 116px;
  display: flex;
  justify-content: flex-start;
`;

export const ColumnTextBox = styled(TextBox)<{ scheme?: string }>`
  text-align: left;
  white-space: normal;
  font-size: 12px !important;

  ${(props) =>
    props.scheme === 'darkUITheme' ||
    props.scheme === 'bluishTheme' ||
    props.scheme === 'deepBlueTheme'
      ? 'color: #000000 !important;'
      : ''}
`;

export const AurReqsTextBox = styled(TextBox)<IToken & { scheme?: string }>`
  text-align: left;
  height: fit-content;
  font-size: 12px !important;

  ${(props) =>
    props.scheme === 'darkUITheme' ||
    props.scheme === 'bluishTheme' ||
    props.scheme === 'deepBlueTheme'
      ? 'color: #000000 !important;'
      : ''}
`;

export const StyledRow = styled(Row)<IToken>`
  margin-bottom: ${(props) => `${props.token.marginMD}px`};
  padding: 0;
  flex-flow: nowrap;
`;

export const FilterButtonContainer = styled('div')`
  margin-bottom: 8px;
  display: flex;
  gap: 8px;
  justify-content: center;
`;

export const FilterTitle = styled('p')`
  text-align: center;
  margin: 0;
`;

export const FilterButton = styled(Button)`
  font-size: 11px;
`;

export const DeleteIcon = styled(DeleteOutlined)`
  font-size: 16px;
`;

export const MergedColumnSmNoPadding = styled('div')<IItemWidth>`
  width: ${(props) => (props.width ? `${props.width}px` : '80px')};
  position: relative;
  padding: 0;

  &:before {
    position: absolute;
    top: 50%;
    inset-inline-end: 0;
    width: 1px;
    height: 1.6em;
    background-color: ${(props) => props.token.colorBgMask};
    transform: translateY(-50%);
    transition: background-color 0.2s;
    content: '';
    display: block;
  }
`;

export const MergeIcon = styled(MergeCellsOutlined)`
  font-size: 12px;
  height: 12px;
  margin-right: 4px;
`;

export const CopyIcon = styled(CopyOutlined)`
  font-size: 12px;
  height: 12px;
  margin-right: 4px;
`;

export const ColumnHeaderContainer = styled('div')<{ scheme?: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 8px;
  padding-left: 50px;

  span {
    color: ${(props) =>
      props.scheme === 'pinkUITheme' || props.scheme === 'lightUITheme'
        ? '#000000'
        : 'inherit'};
  }
`;

export const ColumnHeaderContainerModule = styled('div')<{ scheme?: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 8px;

  span {
    color: ${(props) =>
      props.scheme === 'pinkUITheme' || props.scheme === 'lightUITheme'
        ? '#000000'
        : 'inherit'};
  }
`;

export const RegulatoryReqHeading = styled.span`
  padding-left: 80px;
`;

export const ModuleHeading = styled.span`
  margin: auto;
  padding-left: 0px;
`;

export const SegmentNoHeading = styled.span`
  margin-left: -20px;
  white-space: pre-line;
  text-align: center;
  line-height: 0.9;
`;

export const TargetArtefactHeading = styled.span`
  margin-left: -50px;
`;

export const InterpretationBaselineHeading = styled.span`
  white-space: nowrap;
  flex-shrink: 0;
`;

export const SearchInput = styled(Input)`
  width: 200px;
  height: 24px;
  margin-left: 10px;

  .ant-input {
    font-size: 12px;
    padding: 2px 6px;
  }
`;

export const ClientSearchInput = styled(Input)`
  width: 200px;
  height: 24px;
  margin-left: 10px;

  .ant-input {
    font-size: 12px;
    padding: 2px 6px;
  }
`;

export const SearchIcon = styled(SearchOutlined)<{
  scheme?: string;
  isAur?: boolean;
}>`
  font-size: 18px;
  cursor: pointer;
  margin-left: ${(props) => (props.isAur ? '50px' : '70px')};
`;

export const ClientSearchIcon = styled(SearchOutlined)<{ scheme?: string }>`
  font-size: 18px;
  color: ${(props) => (props.scheme === 'darkUITheme' ? '#ffffff' : '#000000')};
  cursor: pointer;
  margin-left: 50px;

  &:hover {
    color: ${(props) =>
      props.scheme === 'darkUITheme' ? '#cccccc' : '#333333'};
  }
`;

export const CategoryHeaderContainer = styled('div')<{ scheme?: string }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  margin-left: 0px;
  position: relative;
  z-index: 1001;
  border-radius: 4px;
  transition: all 0.2s;
  user-select: none;
  cursor: pointer;
  border: 1px solid transparent;
  background-color: transparent;
  min-width: 100px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  &:active {
    background-color: rgba(0, 0, 0, 0.06);
  }

  span {
    color: ${(props) =>
      props.scheme === 'pinkUITheme' || props.scheme === 'lightUITheme'
        ? '#000000'
        : 'inherit'};
    font-size: 13px;
    white-space: nowrap;
  }

  /* Ensure dropdown appears above other elements */
  &:has(.category-filter-dropdown) {
    z-index: 1002;
    background-color: rgba(0, 0, 0, 0.06);
  }
`;

export const CategoryFilterIcon = styled(FilterOutlined)<{ scheme?: string }>`
  font-size: 12px;
  color: ${(props) => (props.scheme === 'darkUITheme' ? '#ffffff' : '#666666')};
  transition: color 0.2s;
  flex-shrink: 0;
  pointer-events: auto;
  cursor: pointer;
`;

export const CategoryFilterDropdown = styled('div')<{
  scheme?: string;
  token?: any;
}>`
  position: fixed;
  background: white;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.08),
    0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05);
  z-index: 99999;
  padding: 4px;
  min-width: 180px;
  max-height: 250px;
  overflow-y: auto;

  /* Use theme text color */
  color: ${(props) => props.token?.colorText || '#000000'};

  /* Force theme text color for all child elements */
  * {
    color: ${(props) => props.token?.colorText || '#000000'};
  }

  .ant-select {
    width: 100%;
    color: ${(props) => props.token?.colorText || '#000000'};

    .ant-select-item,
    .ant-select-item-option,
    .ant-select-selection-item {
      color: ${(props) => props.token?.colorText || '#000000'};
    }
  }

  .ant-select-dropdown {
    position: static;
    box-shadow: none;
    border: none;
    color: ${(props) => props.token?.colorText || '#000000'};

    .ant-select-item {
      color: ${(props) => props.token?.colorText || '#000000'};
    }
  }
`;

export const CategoryFilterOption = styled('div')<{
  selected?: boolean;
  scheme?: string;
  token?: any;
}>`
  padding: 6px 10px;
  cursor: pointer;
  border-radius: 3px;
  font-size: 12px;
  background-color: ${(props) => (props.selected ? '#f0f0f0' : 'transparent')};
  color: ${(props) => props.token?.colorText || '#000000'};
  border-bottom: 1px solid #f0f0f0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${(props) => (props.selected ? '#d0d0d0' : '#f5f5f5')};
    color: ${(props) => props.token?.colorText || '#000000'};
  }
`;

export const SearchContainer = styled('div')<{ isAurSearch?: boolean }>`
  display: flex;
  align-items: center;
  margin-left: ${(props) => (props.isAurSearch ? '0' : '50px')};
  position: relative;
  z-index: 15;
`;

export const CategoryFilterSeparator = styled('div')`
  height: 1px;
  background-color: #e8e8e8;
  margin: 2px 0;
`;

export const AurHeaderSpan = styled.span<{ scheme?: string }>`
  min-width: 380px;
  display: inline-block;
  margin-left: -40px;
  color: ${(props) =>
    props.scheme === 'pinkUITheme' || props.scheme === 'lightUITheme'
      ? '#000000'
      : 'inherit'};
`;

export const HeaderWithSearch = styled('div')<{ scheme?: string }>`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 6px 10px;
  padding-left: 180px;
  padding-right: 20px;
  position: relative;
  z-index: 10;
  overflow: visible;
  width: auto;
  flex: 1;

  span {
    color: ${(props) =>
      props.scheme === 'pinkUITheme' || props.scheme === 'lightUITheme'
        ? '#000000'
        : 'inherit'};
    flex-shrink: 0;
  }
`;

export const ClientReqTextArea = styled(TextArea)<{
  level?: number;
  width?: number;
  scheme?: string;
  token?: unknown;
}>`
  width: ${({ width = 445 }) => width}px;
  resize: both;
  min-width: 200px;
  max-width: 1200px;
  overflow: auto;

  &.ant-input {
    background-color: #ffffff;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
  }

  /* Force background and border on all states */
  &,
  &:hover,
  &:focus,
  &:active {
    background-color: #ffffff;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
  }

  &.bru-field {
    width: 100px;
    resize: none;
  }

  ${(props) =>
    props.scheme === 'darkUITheme' ||
    props.scheme === 'bluishTheme' ||
    props.scheme === 'deepBlueTheme' ||
    props.scheme === 'deepNavyTheme'
      ? 'color: #000000 !important;'
      : ''}

  /* Level 1 */
  &[data-level="1"] {
    font-size: 14px;
    font-weight: bold;
  }

  /* Level 2 */
  &[data-level='2'] {
    font-size: 13px;
    font-weight: normal;
  }

  /* Level 3 */
  &[data-level='3'] {
    font-size: 12px;
    font-weight: normal;
  }

  /* Level 4 */
  &[data-level='4'] {
    font-size: 11px;
    font-weight: normal;
  }

  /* Level 5+ */
  &[data-level='5'] {
    font-size: 10px;
    font-weight: normal;
  }

  /* Scrollbar styling using consistent color constants */
  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  /* Track */
  &::-webkit-scrollbar-track {
    background: linear-gradient(
      135deg,
      ${(props) => getThemeValues(props.scheme, props.token).bgContainer},
      ${(props) => getThemeValues(props.scheme, props.token).bgMask}
    );
    border-radius: 10px;
    box-shadow: inset 0 0 4px rgba(0, 0, 0, 0.15);
  }

  /* Thumb */
  &::-webkit-scrollbar-thumb {
    background: ${(props) =>
      getThemeValues(props.scheme, props.token).primaryColor};
    border-radius: 10px;
    border: 2px solid
      ${(props) => getThemeValues(props.scheme, props.token).bgContainer};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
    transition: background 0.2s ease, box-shadow 0.2s ease;
  }

  /* Hover state */
  &::-webkit-scrollbar-thumb:hover {
    background: ${(props) =>
      getThemeValues(props.scheme, props.token).primaryColor};
    opacity: 0.8;
  }

  /* Active state */
  &::-webkit-scrollbar-thumb:active {
    background: ${(props) =>
      getThemeValues(props.scheme, props.token).primaryColor};
    opacity: 0.9;
  }

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: ${(props) =>
      getThemeValues(props.scheme, props.token).primaryColor}
    ${(props) => getThemeValues(props.scheme, props.token).bgMask};
`;

/* AUR TextArea with resize behavior matching Client Interpretation Baseline */
export const AurTextArea = styled(TextArea)<{
  scheme?: string;
  token?: unknown;
}>`
  width: 100%;
  max-width: 800px;
  min-width: 330px;
  min-height: 80px;
  resize: both;
  overflow: auto;
  color: #000000 !important;
  border: 1px solid #d9d9d9;
  background-color: #ffffff;
  box-sizing: border-box;

  &.ant-input {
    resize: both;
    overflow: auto;
    color: #000000 !important;
    border: 1px solid #d9d9d9;
    background-color: #ffffff;
  }

  &,
  &:hover,
  &:focus,
  &:active {
    resize: both;
    overflow: auto;
    color: #000000 !important;
    border: 1px solid #d9d9d9;
    background-color: #ffffff;
  }

  /* Scrollbar styling using consistent color constants */
  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  /* Track */
  &::-webkit-scrollbar-track {
    background: linear-gradient(
      135deg,
      ${(props) => getThemeValues(props.scheme, props.token).bgContainer},
      ${(props) => getThemeValues(props.scheme, props.token).bgMask}
    );
    border-radius: 10px;
    box-shadow: inset 0 0 4px rgba(0, 0, 0, 0.15);
  }

  /* Thumb */
  &::-webkit-scrollbar-thumb {
    background: ${(props) =>
      getThemeValues(props.scheme, props.token).primaryColor};
    border-radius: 10px;
    border: 2px solid
      ${(props) => getThemeValues(props.scheme, props.token).bgContainer};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
    transition: background 0.2s ease, box-shadow 0.2s ease;
  }

  /* Hover state */
  &::-webkit-scrollbar-thumb:hover {
    background: ${(props) =>
      getThemeValues(props.scheme, props.token).primaryColor};
    opacity: 0.8;
  }

  /* Active state */
  &::-webkit-scrollbar-thumb:active {
    background: ${(props) =>
      getThemeValues(props.scheme, props.token).primaryColor};
    opacity: 0.9;
  }

  scrollbar-width: thin;
  scrollbar-color: ${(props) =>
      getThemeValues(props.scheme, props.token).primaryColor}
    ${(props) => getThemeValues(props.scheme, props.token).bgMask};
`;

export const NumberSpan = styled.span<{ scheme?: string }>`
  color: ${(props) =>
    props.scheme === 'darkUITheme' ||
    props.scheme === 'bluishTheme' ||
    props.scheme === 'deepBlueTheme'
      ? '#000000 !important'
      : 'inherit'};
`;

export const TextSpan = styled.span<{ scheme?: string }>`
  color: ${(props) =>
    props.scheme === 'darkUITheme' ||
    props.scheme === 'bluishTheme' ||
    props.scheme === 'deepBlueTheme'
      ? '#000000 !important'
      : 'inherit'};
`;

export const ParsedTextContainer = styled.div<{
  level?: number;
  width?: number;
  scheme?: string;
  token?: unknown;
}>`
  padding: 4px 11px;
  min-height: 240px;
  height: auto;
  overflow-y: auto;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background-color: #ffffff;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, sans-serif;
  line-height: 1.5715;
  font-size: 14px;
  display: block;

  width: ${({ width = 445 }) => width}px;
  min-width: 445px;
  overflow-x: auto;
  overflow-y: auto;
  resize: both;

  /* Scrollbar styling using dynamic theme colors */
  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: linear-gradient(
      135deg,
      ${(props) => getThemeValues(props.scheme, props.token).bgContainer},
      ${(props) => getThemeValues(props.scheme, props.token).bgMask}
    );
    border-radius: 10px;
    box-shadow: inset 0 0 4px rgba(0, 0, 0, 0.15);
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props) =>
      getThemeValues(props.scheme, props.token).primaryColor};
    border-radius: 10px;
    border: 2px solid
      ${(props) => getThemeValues(props.scheme, props.token).bgContainer};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
    transition: background 0.2s ease, box-shadow 0.2s ease;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${(props) =>
      getThemeValues(props.scheme, props.token).primaryColor};
    opacity: 0.8;
  }

  &::-webkit-scrollbar-thumb:active {
    background: ${(props) =>
      getThemeValues(props.scheme, props.token).primaryColor};
    opacity: 0.9;
  }

  scrollbar-width: thin;
  scrollbar-color: ${(props) =>
      getThemeValues(props.scheme, props.token).primaryColor}
    ${(props) => getThemeValues(props.scheme, props.token).bgMask};

  ${(props) =>
    props.scheme === 'darkUITheme' ||
    props.scheme === 'bluishTheme' ||
    props.scheme === 'deepBlueTheme' ||
    props.scheme === 'deepNavyTheme'
      ? 'color: #000000 !important;'
      : ''}
`;

export const ParsedTextBox = styled(TextBox)`
  text-align: left;
  white-space: normal;
  font-size: 16px !important;
`;

export const ParsedAurTextBox = styled(TextBox)<IToken>`
  text-align: left;
  height: fit-content;
  font-size: 16px !important;
`;

export const StyledCustomSelect = styled(CustomSelect)`
  width: 150px;
`;

export const StyledCustomTable = styled(CustomTable)<{
  hasVisibleDropdowns?: boolean;
}>`
  .ant-table-wrapper {
    .ant-table {
      margin: unset !important;
      min-height: 200px;
    }
  }

  .ant-spin-nested-loading {
    overflow-y: initial !important;
  }

  /* Add minimum height when dropdowns are visible to prevent cutoff */
  ${(props) =>
    props.hasVisibleDropdowns &&
    `
    .ant-table-content {
      min-height: 800px !important;
    }
    
    .ant-table-tbody {
      min-height: 200px;
    }
  `}
`;

/* Global CSS for dark theme text override */
export const GlobalStyle = `
  .dark-theme-text {
    color: #000000 !important;
  }
`;

export const RiskControlWrapper = styled('div')`
  display: flex;
  gap: 0;
  align-items: center;
  width: 100%;
`;

export const ContentTypeSelect = styled('div')`
  width: 200px;
  margin-bottom: 8px;

  .ant-select {
    width: 100% !important;
  }

  .ant-select-selector {
    width: 100% !important;
    min-width: 200px !important;
  }

  .ant-select-selection-search {
    width: 100% !important;
  }

  .ant-select-selection-item {
    width: calc(100% - 24px) !important;
  }
`;

export const DefinitionInput = styled(Input)`
  width: 200px;
  height: 32px;
`;

export const ClientReqColumn = styled('div')`
  padding-left: 20px;
`;
export const ValidityDateWrapper = styled('div')`
  display: flex;
  gap: 8px;
  width: 100%;
  margin-left: -15px;
`;

export const DateInput = styled(Input)`
  flex: 1;
  font-size: 12px;
`;

export const ApplicabilityFilterDropdown = styled('div')`
  padding: 12px;
  min-width: 200px;
`;

export const ApplicabilityFilterIcon = styled('div')`
  cursor: pointer;
  font-size: 11px;
  padding: 8px 16px;
  min-width: 80px;
  min-height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: 4px;
  background-color: transparent;
  transition: all 0.2s;
  user-select: none;
  margin-left: -20px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
    border-color: #d9d9d9;
  }

  &:active {
    background-color: rgba(0, 0, 0, 0.08);
  }
`;

export const ScopeHeaderContainer = styled('div')<{ scheme?: string }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 8px;
  margin-left: -80px;
  position: relative;
  z-index: 9998;
  border-radius: 4px;
  transition: all 0.2s;
  user-select: none;
  cursor: pointer;
  border: 1px solid transparent;
  background-color: transparent;
  min-width: 120px;
  max-width: 120px;
  overflow: visible;
  pointer-events: auto;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
    border: 1px solid transparent;
  }

  &:active {
    background-color: rgba(0, 0, 0, 0.06);
  }

  span {
    color: ${(props) =>
      props.scheme === 'pinkUITheme' || props.scheme === 'lightUITheme'
        ? '#000000'
        : 'inherit'};
    font-size: 12px;
    white-space: nowrap;
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    pointer-events: none;
  }

  svg {
    pointer-events: none;
  }

  /* Ensure dropdown appears above other elements */
  &:has(.scope-filter-dropdown) {
    z-index: 9999;
    background-color: rgba(0, 0, 0, 0.06);
  }
`;

export const ScopeFilterDropdown = styled('div')`
  position: fixed;
  background: white;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.08),
    0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05);
  z-index: 99999;
  padding: 4px;
  min-width: 120px;
  max-height: 200px;
  overflow-y: auto;
`;

export const ScopeFilterOption = styled('div')<{ selected?: boolean }>`
  padding: 6px 10px;
  cursor: pointer;
  border-radius: 3px;
  font-size: 12px;
  background-color: ${(props) => (props.selected ? '#f0f0f0' : 'transparent')};
  color: #000000;
  border-bottom: 1px solid #f0f0f0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${(props) => (props.selected ? '#d0d0d0' : '#f5f5f5')};
    color: #000000;
  }
`;

export const ScopeFilterIcon = styled(FilterOutlined)<{ scheme?: string }>`
  font-size: 12px;
  color: ${(props) => (props.scheme === 'darkUITheme' ? '#ffffff' : '#666666')};
  transition: color 0.2s;
  flex-shrink: 0;
  cursor: pointer;
`;

export const ScopeFilterContainer = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: -15px;
  padding: 4px 8px;
  border-radius: 4px;
`;

export const ScopeText = styled('span')`
  font-size: 11px;
  user-select: none;
  color: inherit;
  white-space: nowrap;
`;

export const ScopeDropdownIcon = styled(DownOutlined)`
  font-size: 10px;
  color: inherit;
  transition: transform 0.2s;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  min-height: 16px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.08);
  }

  /* Add data attribute for CSS targeting */
  &[data-scope-icon] {
    display: inline-flex;
  }
`;

export const ApplicabilityText = styled('span')<{ scheme?: string }>`
  display: inline-block;
  margin-right: 12px;
  // margin-left: 5px;
  cursor: pointer;
  user-select: none;
  font-size: 11px;
  position: relative;
  top: -3px;
  color: ${(props) => (props.scheme === 'lilacTheme' ? '#000000' : 'inherit')};

  &:hover {
    opacity: 0.8;
  }
`;

export const CategoryContainer = styled('div')`
  margin-left: 0px;
`;

export const CategoryDropdownContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-height: 40px;
  justify-content: flex-start;
  padding-top: 4px;

  & > *:first-child {
    margin-top: -4px;
  }
`;

export const CategoryDropdownWrapper = styled('div')<{ isLast?: boolean }>`
  margin-bottom: ${(props) => (props.isLast ? '0' : '16px')};
`;

export const EmptyCategoryContainer = styled('div')`
  min-height: 40px;
  display: flex;
  align-items: flex-start;
  padding-top: 4px;
`;

export const TitleTextLine = styled.div<IToken & { scheme?: string }>`
  font-weight: 800;
  margin-bottom: 4px;
  color: ${(props) => props.token.colorText} !important;
`;

export const NumberedParagraphLine = styled.div<IToken & { scheme?: string }>`
  font-weight: 700;
  margin-bottom: 4px;
  color: ${(props) => props.token.colorText} !important;
`;

export const SubItemLine = styled.div<IToken & { scheme?: string }>`
  font-weight: 400;
  margin-bottom: 4px;
  color: ${(props) => props.token.colorText} !important;
`;

export const TargetArtefactContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-left: -90px;
`;

export const ContentRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
`;
export const ModuleBadge = styled.div<{
  scheme: string;
  token: any;
}>`
  flex-shrink: 0;

  width: 150px;

  padding: 4px 10px;
  border-radius: 5px;

  font-size: 14px;
  font-weight: 600;
  line-height: 2;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  background: ${({ token }) => token.colorFillQuaternary};
  color: ${({ scheme }) =>
    scheme === 'bluishTheme' ? '#ffffff !important' : 'inherit'};
`;

export const DropdownPortal = styled.div`
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  background: white;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.08),
    0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05);
  z-index: 9999;
  padding: 4px;
  min-width: 120px;
  max-height: 200px;
  overflow-y: auto;
`;

export const CategoryDropdownPortal = styled(DropdownPortal)`
  min-width: 180px;
  max-height: 250px;
  z-index: 99999;
  top: calc(100% + 8px);
  left: -50px;
`;

export const DropdownOption = styled.div<{ selected?: boolean }>`
  padding: 8px 12px;
  cursor: pointer;
  font-size: 12px;
  background-color: ${(props) => (props.selected ? '#e6f7ff' : 'transparent')};
  color: #000000;
  border-bottom: 1px solid #f0f0f0;
  user-select: none;
  transition: background-color 0.2s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${(props) => (props.selected ? '#e6f7ff' : '#f5f5f5')};
  }
`;

export const AurHeadingWrapper = styled('div')<{ dragOffset?: number }>`
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
  display: block;
  position: relative;
  left: ${(props) => (props.dragOffset ? `${props.dragOffset}px` : '0')};
  transition: left 0.05s ease;
`;
